import { ApiError } from "@/lib/api"
import { logger } from "@/lib/logger"
import { toast } from "@/components/ui/use-toast"

/**
 * Que hacer cuando una escritura falla.
 *
 * El caso peor de esta SPA no es que algo falle: es que falle y el usuario crea
 * que se guardo. Un maestro que no se guardo cambia lo que la proyeccion de caja
 * calcula manana, y nadie va a volver a mirar la pantalla para comprobarlo.
 *
 * Por eso todo fallo de escritura hace tres cosas a la vez, siempre:
 *
 * 1. **Se dice en pantalla**, con la misma regla que `DataState`: que fallo, que
 *    significa y como seguir. Y una frase que no puede faltar: el cambio NO
 *    quedo guardado.
 * 2. **Se registra**, con metodo, ruta, estado y `request_id`. El toast dura seis
 *    segundos; el log queda.
 * 3. **Se muestra la referencia** cuando el backend la dio. Es lo que convierte
 *    "no me guardo" en un incidente que se puede investigar.
 *
 * Se usa como `onError` de cualquier mutacion:
 *
 *     useMutation({ mutationFn: ..., onError: onWriteError("el cliente") })
 */

interface Contexto {
  /** Que se estaba guardando, en minusculas y con articulo: "el cliente". */
  entidad: string
  /** El verbo, si no es guardar: "eliminar", "reemplazar". */
  operacion?: string
}

function guiaPorEstado(error: ApiError): string {
  const detalle = typeof error.detail === "string" && error.detail.trim() ? error.detail.trim() : ""
  switch (error.status) {
    case 401:
      return "Tu sesion expiro antes de que el cambio llegara. Vuelve a iniciar sesion y repitelo: no se guardo nada."
    case 403:
      return "Tu rol no incluye esta capacidad en la entidad activa. Pide el alcance a quien administra la cartera, o cambia de entidad en la barra superior."
    case 404:
      return "El registro ya no existe o pertenece a otra entidad. Recarga la lista antes de volver a intentarlo."
    case 409:
      return (
        detalle ||
        "Choca con el estado actual: lo mas probable es que el periodo este cerrado. Un periodo cerrado no se edita; la correccion va por asiento en un periodo posterior."
      )
    case 422:
      return detalle
        ? `El backend rechazo los datos: ${detalle}`
        : "El backend rechazo los datos. Revisa los campos obligatorios y el motivo antes de reintentar."
    default:
      if (error.status >= 500) {
        return "Fallo del servidor, no de lo que hiciste. Reintenta en un momento; si vuelve a fallar, reporta el incidente citando la referencia."
      }
      return detalle || error.message
  }
}

function esFalloDeRed(error: unknown): boolean {
  const mensaje = error instanceof Error ? error.message : String(error ?? "")
  return /failed to fetch|networkerror|network request failed|load failed|fetch/i.test(mensaje)
}

export function reportWriteError(error: unknown, contexto: Contexto): void {
  const verbo = contexto.operacion ?? "guardar"
  const titulo = `No se pudo ${verbo} ${contexto.entidad}`

  let descripcion: string
  let requestId: string | null = null

  if (error instanceof ApiError) {
    requestId = error.requestId
    descripcion = guiaPorEstado(error)
  } else if (esFalloDeRed(error)) {
    descripcion =
      "No se pudo alcanzar el servidor. Revisa tu conexion y reintenta: el cambio no quedo guardado."
  } else {
    descripcion =
      error instanceof Error && error.message
        ? error.message
        : "Fallo inesperado en la aplicacion. Reintenta; si persiste, reporta la pantalla y la hora."
  }

  // La API ya se logueo a si misma en `request()`; esto agrega lo que alli no se
  // sabe: QUE operacion de negocio quedo sin hacer.
  logger.error("fallo una escritura", {
    operacion: verbo,
    entidad: contexto.entidad,
    estado: error instanceof ApiError ? error.status : null,
    request_id: requestId,
    tipo: error instanceof ApiError ? "http" : esFalloDeRed(error) ? "red" : "cliente",
    stack: error instanceof Error ? error.stack : undefined,
  })

  toast({
    variant: "destructive",
    title: titulo,
    description: requestId ? `${descripcion} (referencia ${requestId})` : descripcion,
  })
}

/** Version lista para `onError` de react-query. */
export function onWriteError(entidad: string, operacion?: string) {
  return (error: unknown) => reportWriteError(error, { entidad, operacion })
}
